import { extractErrorMessage } from "@platform/api";
import { useNotification } from "@platform/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import groupBy from "lodash/groupBy";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { queryKeys } from "../../../api/queryKeys";
import {
  createFieldComment,
  deleteFieldComment,
  updateFieldComment,
  upsertFieldComments,
} from "../../../api/clientFieldComments";
import { useApiClient } from "../../../api/useApiClient";

import type {
  CreateFieldCommentRequest,
  FieldCommentResponse,
  UpdateFieldCommentRequest,
} from "../../../api/fieldCommentTypes";
import type { BenefitPlanGroup } from "../../../api/types";
import type { ReviewMenuTarget } from "../components/ContextMenu/reviewMenuItems";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Indexed field comment lookup: planId → fieldPath → FieldCommentResponse.
 * This structure allows O(1) lookups for any cell in the grid.
 */
export type FieldCommentsIndex = Record<
  string,
  Record<string, FieldCommentResponse>
>;

type FieldCell = { planId: string; fieldPath: string };

export interface FieldMetadataContextValue {
  /** Replace the entire BPG-derived index (e.g., after a full refresh). */
  setBenefitPlanGroups: (bpgs?: BenefitPlanGroup[]) => void;

  /** Return the full indexed comment lookup for read-only consumers. */
  getFieldCommentsIndex: () => FieldCommentsIndex;

  /** Look up the field comment for a specific plan + field, if any. */
  getFieldComment: (
    planId: string,
    fieldPath: string,
  ) => FieldCommentResponse | undefined;

  /** Optimistically create a field comment and persist to the API. */
  createComment: (
    planId: string,
    fieldPath: string,
    comment: string | null,
  ) => void;

  /** Optimistically update an existing field comment and persist to the API. */
  updateComment: (
    planId: string,
    fieldCommentId: string,
    data: UpdateFieldCommentRequest,
  ) => void;

  /** Optimistically remove a field comment and persist to the API. */
  removeComment: (planId: string, fieldCommentId: string) => void;

  /**
   * Mark a field as manually edited. If a field comment already exists for this
   * field, updates it; otherwise creates a new one with manuallyEdited: true.
   * Pass isBenchmarkField: true when the edited cell was populated by benchmark data.
   */
  markManuallyEdited: (
    planId: string,
    fieldPath: string,
    options?: { isBenchmarkField?: boolean; aiOriginalValue?: string },
  ) => void;

  /**
   * Clear the manually-edited flag on a field (e.g. when the user reverts
   * a value back to the AI-extracted original). No-ops if the field has no
   * existing comment or is not currently marked as manually edited.
   */
  unmarkManuallyEdited: (planId: string, fieldPath: string) => void;

  /**
   * Set or clear the validated flag on cells in one request, across any of
   * the RFP's plans. Creates the record when a cell has none yet; cells
   * already in the requested state, and cells with no record being cleared,
   * are left alone.
   */
  setValidated: (targets: ReviewMenuTarget[], isValidated: boolean) => void;

  /**
   * Handle an AI-populated field being edited. On the first edit, stores
   * the pre-edit value and marks the field as manually edited. On subsequent
   * edits, compares the new value against the stored original and unmarks
   * if the user reverted to the original AI-populated value.
   */
  handleAiFieldEdited: (
    planId: string,
    fieldPath: string,
    newValue: unknown,
    oldValue: unknown,
  ) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const FieldMetadataContext = createContext<FieldMetadataContextValue | null>(
  null,
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build an indexed lookup from the flat BPG plan structure. */
const buildIndexFromBpg = (
  benefitPlanGroups: BenefitPlanGroup[] | undefined,
): FieldCommentsIndex => {
  const index: FieldCommentsIndex = {};
  if (!benefitPlanGroups) return index;
  for (const group of benefitPlanGroups) {
    for (const plan of group.benefitPlans) {
      for (const fc of plan.fieldComments ?? []) {
        if (!fc.fieldPath) continue;
        if (!index[plan.id]) index[plan.id] = {};
        index[plan.id][fc.fieldPath] = fc;
      }
    }
  }
  return index;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface FieldMetadataProviderProps {
  /** BTX (V5) RFP id; bulk writes are scoped to it. */
  ecoRfpId: string;
  benefitPlanGroups?: BenefitPlanGroup[];
  children: React.ReactNode;
}

export const FieldMetadataProvider: React.FC<FieldMetadataProviderProps> = ({
  ecoRfpId,
  benefitPlanGroups,
  children,
}) => {
  const axiosInstance = useApiClient();
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const optimisticIdCounter = useRef(0);

  // ---- State: planId → fieldPath → FieldCommentResponse ----
  const [comments, setComments] = useState<FieldCommentsIndex>({});
  // Keep a ref in sync so that callbacks called from stale closures
  // (e.g. AG Grid event handlers) always read the latest comments.
  const commentsRef = useRef<FieldCommentsIndex>(comments);
  commentsRef.current = comments;

  // Sync from BPG whenever it changes (server truth).
  // Skip when the prop is undefined — in that case data arrives via
  // setBenefitPlanGroups() called from a child component, and running
  // here with undefined would wipe comments that the child already set
  // (child effects fire before parent effects in React).
  useEffect(() => {
    if (!benefitPlanGroups) return;
    const next = buildIndexFromBpg(benefitPlanGroups);
    setComments(next);
  }, [benefitPlanGroups]);

  const setBenefitPlanGroups = useCallback((bpgs?: BenefitPlanGroup[]) => {
    const next = buildIndexFromBpg(bpgs);
    setComments(next);
  }, []);

  const invalidateBpg = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.benefitPlanGroupsAll,
    });
  }, [queryClient]);

  const rollbackCells = useCallback(
    (snapshot: FieldCommentsIndex, cells: FieldCell[]) => {
      setComments((prev) =>
        cells.reduce((next, { planId, fieldPath }) => {
          const before = snapshot[planId]?.[fieldPath];
          const planEntries = { ...next[planId] };

          if (before) {
            planEntries[fieldPath] = before;
          } else {
            delete planEntries[fieldPath];
          }

          return { ...next, [planId]: planEntries };
        }, prev),
      );
    },
    [],
  );

  const handleApiError = useCallback(
    (error: unknown, snapshot: FieldCommentsIndex, cells: FieldCell[]) => {
      const msg = extractErrorMessage(error as Error);
      showError(msg, false);
      rollbackCells(snapshot, cells);
      invalidateBpg();
    },
    [showError, rollbackCells, invalidateBpg],
  );

  // ---- Lookup ----
  const getFieldComment = useCallback(
    (planId: string, fieldPath: string): FieldCommentResponse | undefined =>
      comments[planId]?.[fieldPath],
    [comments],
  );

  const getFieldCommentsIndex = useCallback(
    // Return the current index directly to avoid deep-cloning on hot paths.
    // Consumers should treat the returned object as read-only and clone only
    // when they need to mutate a local copy.
    (): FieldCommentsIndex => comments,
    [comments],
  );

  // ---- Optimistic create ----
  // Shared by every caller that needs a record for a cell that has none yet:
  // an analyst's review, a manual-edit marker.
  const createRecord = useCallback(
    (
      planId: string,
      fieldPath: string,
      fields: Omit<FieldCommentResponse, "id" | "fieldPath">,
      request: Omit<CreateFieldCommentRequest, "fieldPath">,
    ) => {
      if (!axiosInstance) return;

      optimisticIdCounter.current += 1;
      const optimisticEntry: FieldCommentResponse = {
        ...fields,
        id: `optimistic-${optimisticIdCounter.current}`,
        fieldPath,
      };

      // Capture snapshot inside the updater so it matches the exact
      // pre-mutation state, even if other mutations are in flight.
      let snapshot: FieldCommentsIndex = {};
      setComments((prev) => {
        snapshot = prev;
        return {
          ...prev,
          [planId]: { ...prev[planId], [fieldPath]: optimisticEntry },
        };
      });

      createFieldComment(planId, { ...request, fieldPath }, axiosInstance)
        .then((created) => {
          // Replace optimistic ID with the real server ID so that
          // subsequent update/delete operations use the correct ID.
          setComments((prev) => {
            const existing = prev[planId]?.[fieldPath];
            if (existing?.id === optimisticEntry.id) {
              return {
                ...prev,
                [planId]: {
                  ...prev[planId],
                  [fieldPath]: { ...existing, id: created.id },
                },
              };
            }
            return prev;
          });
          invalidateBpg();
        })
        .catch((err) => {
          if (err instanceof AxiosError && err.response?.status === 409) {
            // Comment already exists on the server (stale local state).
            // Refresh so the UI reflects truth; the user can retry.
            invalidateBpg();
          } else {
            handleApiError(err, snapshot, [{ planId, fieldPath }]);
          }
        });
    },
    [axiosInstance, invalidateBpg, handleApiError],
  );

  const createComment = useCallback(
    (planId: string, fieldPath: string, comment: string | null) => {
      createRecord(
        planId,
        fieldPath,
        {
          comment,
          needsReview: true,
          manuallyEdited: false,
          isBenchmarkField: false,
          isValidated: false,
        },
        { comment, needsReview: true },
      );
    },
    [createRecord],
  );

  // ---- Optimistic update ----
  const updateCommentFn = useCallback(
    (
      planId: string,
      fieldCommentId: string,
      data: UpdateFieldCommentRequest,
    ) => {
      if (!axiosInstance) return;

      // Perform lookup inside the state updater to avoid stale writes
      let snapshot: FieldCommentsIndex = {};
      let touched: FieldCell[] = [];
      setComments((prev) => {
        snapshot = prev;
        const planComments = prev[planId];
        if (!planComments) return prev;

        const entry = Object.entries(planComments).find(
          ([, fc]) => fc.id === fieldCommentId,
        );
        if (!entry) return prev;

        const [fieldPath, existing] = entry;
        touched = [{ planId, fieldPath }];
        return {
          ...prev,
          [planId]: {
            ...planComments,
            [fieldPath]: { ...existing, ...data },
          },
        };
      });

      updateFieldComment(planId, fieldCommentId, data, axiosInstance)
        .then(() => invalidateBpg())
        .catch((err) => handleApiError(err, snapshot, touched));
    },
    [axiosInstance, invalidateBpg, handleApiError],
  );

  // ---- Optimistic remove ----
  const removeComment = useCallback(
    (planId: string, fieldCommentId: string) => {
      if (!axiosInstance) return;

      // Perform lookup inside the state updater to avoid stale writes
      let snapshot: FieldCommentsIndex = {};
      let touched: FieldCell[] = [];
      setComments((prev) => {
        snapshot = prev;
        const planComments = prev[planId];
        if (!planComments) return prev;

        const entry = Object.entries(planComments).find(
          ([, fc]) => fc.id === fieldCommentId,
        );
        if (!entry) return prev;

        const [fieldPath] = entry;
        touched = [{ planId, fieldPath }];
        const nextPlan = { ...planComments };
        delete nextPlan[fieldPath];
        return { ...prev, [planId]: nextPlan };
      });

      deleteFieldComment(planId, fieldCommentId, axiosInstance)
        .then(() => invalidateBpg())
        .catch((err) => handleApiError(err, snapshot, touched));
    },
    [axiosInstance, invalidateBpg, handleApiError],
  );

  // ---- Mark manually edited ----
  const markManuallyEdited = useCallback(
    (
      planId: string,
      fieldPath: string,
      options?: { isBenchmarkField?: boolean; aiOriginalValue?: string },
    ) => {
      if (!axiosInstance) return;

      // Read from ref so this works even when called from a stale AG Grid
      // event handler that captured an older version of this callback.
      const existing = commentsRef.current[planId]?.[fieldPath];

      if (existing?.manuallyEdited) return; // already marked

      if (existing) {
        // Update existing field comment
        const updateData: UpdateFieldCommentRequest = { manuallyEdited: true };
        if (options?.isBenchmarkField) {
          updateData.isBenchmarkField = true;
        }
        if (options?.aiOriginalValue !== undefined) {
          updateData.aiOriginalValue = options.aiOriginalValue;
        }
        updateCommentFn(planId, existing.id, updateData);
        return;
      }

      // Create a new field comment with only manuallyEdited set
      createRecord(
        planId,
        fieldPath,
        {
          comment: null,
          needsReview: false,
          manuallyEdited: true,
          isBenchmarkField: Boolean(options?.isBenchmarkField),
          isValidated: false,
          aiOriginalValue: options?.aiOriginalValue ?? null,
        },
        {
          needsReview: false,
          manuallyEdited: true,
          ...(options?.isBenchmarkField && { isBenchmarkField: true }),
          ...(options?.aiOriginalValue !== undefined && {
            aiOriginalValue: options.aiOriginalValue,
          }),
        },
      );
    },
    [axiosInstance, updateCommentFn, createRecord],
  );

  // ---- Unmark manually edited ----
  const unmarkManuallyEdited = useCallback(
    (planId: string, fieldPath: string) => {
      if (!axiosInstance) return;

      const existing = commentsRef.current[planId]?.[fieldPath];
      if (!existing?.manuallyEdited) return; // nothing to clear

      updateCommentFn(planId, existing.id, {
        manuallyEdited: false,
        aiOriginalValue: "",
      });
    },
    [axiosInstance, updateCommentFn],
  );

  // ---- Set validated ----
  const setValidated = useCallback(
    (targets: ReviewMenuTarget[], isValidated: boolean) => {
      if (!axiosInstance) return;

      // Validating settles a cell, so it drops out of review, and clearing
      // the flag puts a cell that kept a comment back into it. A comment
      // never stands on a cell that carries neither flag. Either way the
      // text itself is left untouched.
      const changes = targets.flatMap(({ planId, fieldPath }) => {
        const existing = commentsRef.current[planId]?.[fieldPath];
        const isAlreadySet = existing
          ? existing.isValidated === isValidated
          : !isValidated;
        if (isAlreadySet) return [];

        const needsReview = isValidated ? false : Boolean(existing?.comment);
        const optimisticEntry = existing
          ? { ...existing, isValidated, needsReview }
          : {
              id: `optimistic-${(optimisticIdCounter.current += 1)}`,
              fieldPath,
              comment: null,
              needsReview,
              manuallyEdited: false,
              isBenchmarkField: false,
              isValidated,
            };
        return [{ planId, fieldPath, needsReview, optimisticEntry }];
      });
      if (changes.length === 0) return;

      let snapshot: FieldCommentsIndex = {};
      setComments((prev) => {
        snapshot = prev;
        return changes.reduce(
          (next, { planId, fieldPath, optimisticEntry }) => ({
            ...next,
            [planId]: { ...next[planId], [fieldPath]: optimisticEntry },
          }),
          prev,
        );
      });

      const benefitPlans = Object.entries(
        groupBy(changes, (change) => change.planId),
      ).map(([benefitPlanId, planChanges]) => ({
        benefitPlanId,
        fields: planChanges.map(({ fieldPath, needsReview }) => ({
          fieldPath,
          isValidated,
          needsReview,
        })),
      }));

      upsertFieldComments(ecoRfpId, { benefitPlans }, axiosInstance)
        .then((written) => {
          // Swap optimistic ids for the server's so later updates and deletes
          // address the real records.
          setComments((prev) =>
            written.benefitPlans.reduce(
              (next, { benefitPlanId, fieldComments }) =>
                fieldComments.reduce((acc, { id, fieldPath }) => {
                  const current = acc[benefitPlanId]?.[fieldPath];
                  if (!current || current.id === id) return acc;
                  return {
                    ...acc,
                    [benefitPlanId]: {
                      ...acc[benefitPlanId],
                      [fieldPath]: { ...current, id },
                    },
                  };
                }, next),
              prev,
            ),
          );
          invalidateBpg();
        })
        .catch((err) =>
          handleApiError(
            err,
            snapshot,
            changes.map(({ planId, fieldPath }) => ({ planId, fieldPath })),
          ),
        );
    },
    [axiosInstance, ecoRfpId, invalidateBpg, handleApiError],
  );

  // ---- Handle AI field edited ----
  // Stores the original (pre-first-edit) grid value for each AI-populated field
  // so we can detect when the user reverts to the AI-populated value.
  // The ref acts as a same-session cache; the persisted aiOriginalValue in the
  // field comment is the cross-session source of truth.
  const aiOriginalValuesRef = useRef<Record<string, unknown>>({});

  const handleAiFieldEdited = useCallback(
    (
      planId: string,
      fieldPath: string,
      newValue: unknown,
      oldValue: unknown,
    ) => {
      const key = `${planId}::${fieldPath}`;
      const existing = commentsRef.current[planId]?.[fieldPath];

      if (!existing?.manuallyEdited) {
        // First edit — oldValue IS the AI-populated grid value.
        // Cache it locally and persist it on the server.
        aiOriginalValuesRef.current[key] = oldValue;
        markManuallyEdited(planId, fieldPath, {
          aiOriginalValue: String(oldValue),
        });
      } else {
        // Already edited — check if reverting to original.
        // Prefer the in-memory cache, fall back to the persisted value.
        const original =
          aiOriginalValuesRef.current[key] ?? existing.aiOriginalValue;
        if (original !== undefined && original !== null) {
          if (String(newValue) === String(original)) {
            delete aiOriginalValuesRef.current[key];
            unmarkManuallyEdited(planId, fieldPath);
          }
        }
      }
    },
    [markManuallyEdited, unmarkManuallyEdited],
  );

  const value = useMemo(
    (): FieldMetadataContextValue => ({
      getFieldCommentsIndex,
      getFieldComment,
      createComment,
      updateComment: updateCommentFn,
      removeComment,
      markManuallyEdited,
      unmarkManuallyEdited,
      setValidated,
      handleAiFieldEdited,
      setBenefitPlanGroups,
    }),
    [
      getFieldCommentsIndex,
      getFieldComment,
      createComment,
      updateCommentFn,
      removeComment,
      markManuallyEdited,
      unmarkManuallyEdited,
      setValidated,
      handleAiFieldEdited,
      setBenefitPlanGroups,
    ],
  );

  return (
    <FieldMetadataContext.Provider value={value}>
      {children}
    </FieldMetadataContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useFieldMetadata = (): FieldMetadataContextValue => {
  const ctx = useContext(FieldMetadataContext);
  if (!ctx) {
    throw new Error(
      "useFieldMetadata must be used within FieldMetadataProvider",
    );
  }
  return ctx;
};
